import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types/next-editor-plugin';
import { Fragment, type Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';

/**
 * Removes textColor marks from a ProseMirror Slice.
 *
 * When users manually copy text from an area that has an explicit text color
 * (e.g. white text on a dark chat-bubble background) and then paste it into
 * the chat input, the color mark is preserved.  Because the input has a light
 * background the text can become invisible.
 *
 * This helper recursively walks every node in the slice and strips the
 * `textColor` mark so that pasted content always uses the editor's default
 * text color.
 */
function removeTextColorFromNode(node: PMNode): PMNode {
	if (node.isText) {
		const filteredMarks = node.marks.filter((mark) => mark.type.name !== 'textColor');
		return node.mark(filteredMarks);
	}

	if (node.content && node.content.size > 0) {
		const children: PMNode[] = [];
		node.content.forEach((child) => {
			children.push(removeTextColorFromNode(child));
		});
		return node.copy(Fragment.from(children));
	}

	return node;
}

function removeTextColorFromSlice(slice: Slice): Slice {
	const { content, openStart, openEnd } = slice;
	const children: PMNode[] = [];
	content.forEach((node) => {
		children.push(removeTextColorFromNode(node));
	});
	return new Slice(Fragment.from(children), openStart, openEnd);
}

/**
 * Editor plugin that strips `textColor` marks from pasted content so that
 * text always renders in the default colour of the chat input.
 */
export const pasteTextColorPlugin: NextEditorPlugin<'pasteTextColorPlugin'> = () => {
	return {
		name: 'pasteTextColorPlugin',
		pmPlugins() {
			return [
				{
					name: 'pasteTextColorPlugin',
					plugin: () => {
						return new SafePlugin({
							props: {
								transformPasted(slice: Slice): Slice {
									return removeTextColorFromSlice(slice);
								},
							},
						});
					},
				},
			];
		},
	};
};
